import React, { createContext, useReducer } from 'react';
import { Auth0Provider, AppState } from '@auth0/auth0-react';

import { AUTH0_REDIRECT_URL, AUTH_ERROR, TOKEN_TYPE, UPDATE_TOKEN, UPDATE_USER } from './constants';
import AuthSession from './AuthSession';
import CypressAuthSession from './AuthSession/Cypress';
import { isForceLoginAutologinUsersActivated } from '~/util/optimizelyHelpers';

interface TokenState {
  token?: string;
  tokenType?: TOKEN_TYPE;
}

interface UserState {
  user: {
    uuid: string | null;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    applicationId: string | null;
  };
}
interface TokenAction {
  type: typeof UPDATE_TOKEN;
  payload: TokenState;
}
interface UserAction {
  type: typeof UPDATE_USER;
  payload: UserState;
}
interface ErrorAction {
  type: typeof AUTH_ERROR;
  payload: {
    error: string;
  };
}

type Action = TokenAction | UserAction | ErrorAction;

interface AuthProviderProps {
  children: React.ReactNode;
}

interface AuthState extends TokenState, UserState {
  error: string | null;
  loading: boolean;
}
interface AuthContextProps {
  auth: AuthState;
  authDispatch?: React.Dispatch<Action>;
}

const initialState: AuthState = {
  token: null,
  error: null,
  loading: true,
  user: {
    uuid: null,
    firstName: null,
    lastName: null,
    email: null,
    applicationId: null,
  },
};

export const AuthContext = createContext<AuthContextProps>({ auth: initialState });

const authReducer = (state: AuthState = initialState, action: Action) => {
  switch (action.type) {
    case UPDATE_TOKEN: {
      const { token, tokenType } = action.payload;

      return { ...state, token, tokenType };
    }
    case UPDATE_USER: {
      const { user } = action.payload;

      return { ...state, user, loading: false };
    }
    case AUTH_ERROR: {
      const { error } = action.payload;

      return { ...state, error, loading: false };
    }
    default:
      // Halt the loading state if an action with unhandled type is passed
      // to prevent any infinite loading scenarios
      return { ...state, loading: false };
  }
};

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [auth, authDispatch] = useReducer(authReducer, initialState);

  /*
    Cypress E2E tests do not support Auth0 requests (https://docs.cypress.io/guides/testing-strategies/auth0-authentication)
    because Cypress does not support requests to non-same-origin domains
    (https://docs.cypress.io/guides/references/trade-offs#Multiple-browsers-open-at-the-same-time).
    Due to this, we need to side-step Auth0 usage when Cypress tests are running.
  */
  if (window.Cypress) {
    return (
      <AuthContext.Provider value={{ auth, authDispatch }}>
        <CypressAuthSession>{children}</CypressAuthSession>
      </AuthContext.Provider>
    );
  }
  const forceLoginActivated = isForceLoginAutologinUsersActivated('');

  const onRedirectCallback = (appState?: AppState) => {
    if (!forceLoginActivated) {
      return;
    }

    const returnTo = appState?.returnTo || window.location.href;

    window.location.replace(returnTo);
  };

  return (
    <Auth0Provider
      domain={window.AUTH0_CLIENT_DOMAIN}
      clientId={window.AUTH0_CLIENT_ID}
      onRedirectCallback={onRedirectCallback}
      redirectUri={AUTH0_REDIRECT_URL}
    >
      <AuthContext.Provider value={{ auth, authDispatch }}>
        <AuthSession>{children}</AuthSession>
      </AuthContext.Provider>
    </Auth0Provider>
  );
};

export default AuthProvider;
